import { useRef, useState, type KeyboardEvent } from 'react'
import InlineRenameInput from '@/components/InlineRenameInput'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { DEFAULT_MAX_STRING_LENGTH, trimAndClamp } from '@/utils/string'

interface FileNameWithCopyProps {
  /** Filename to render + copy. Empty / nullish renders nothing. */
  name: string | null | undefined
  /**
   * Tailwind classes applied to the name text (and the rename input, so the
   * field doesn't resize when editing). Controls font sizing / weight / layout.
   * Defaults match the `<h2 class="text-lg font-semibold truncate">` styling the
   * doc-viewer headers used before this component existed.
   */
  className?: string
  /** Optional override for the icon size — defaults to `w-4 h-4`. */
  iconClassName?: string
  /**
   * When provided, the filename becomes click-to-rename, matching the board-name
   * affordance: clicking the name opens an inline input (auto-focused + selected),
   * Enter / blur commits, Escape cancels, and the copy icon moves to the right so
   * the name itself is the rename target. Receives the sanitized new name and is
   * only called when it differs from the current name. When omitted, the component
   * keeps its original copy-on-click behavior (fully backward compatible).
   */
  onRename?: (newName: string) => void
  /** Max length for the rename input. Defaults to `DEFAULT_MAX_STRING_LENGTH`. */
  maxLength?: number
  /**
   * Stop click / pointer propagation on every interactive element. Needed when
   * the control lives inside a clickable row or the tldraw canvas, where a bubbled
   * click would navigate away or grab the shape.
   */
  stopPropagation?: boolean
}

/**
 * Filename display with a hover-revealed copy icon and optional inline rename,
 * mirroring the board-name affordance (see `BoardTopBarV2`) so the same gesture
 * set — click to rename, hover to copy — works wherever a filename is shown
 * (board card header, file-viewer top bar, breadcrumb labels).
 *
 * Renders nothing when `name` is empty so callers can render it unconditionally.
 */
export default function FileNameWithCopy({
  name,
  className,
  iconClassName,
  onRename,
  maxLength = DEFAULT_MAX_STRING_LENGTH,
  stopPropagation,
}: FileNameWithCopyProps) {
  const { t } = useTranslation(['board'])
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  // The name as it was when editing started — commit compares against this, not
  // the live `name` prop, so a rename only fires when the user actually changed
  // the value (and an external `name` update mid-edit can't swallow the change).
  const editingFromRef = useRef('')
  // Prevent a blur firing right after Enter/Escape from committing twice.
  const actionInProgressRef = useRef(false)

  if (!name) {
    return null
  }

  const stop = (e: { stopPropagation: () => void }) => {
    if (stopPropagation) e.stopPropagation()
  }

  const copyName = () => {
    navigator.clipboard.writeText(name)
    toast.success(t('board:toasts.filename_copied', { defaultValue: 'Filename copied' }))
  }

  // ── Legacy mode: no rename wired → original copy-on-click button ──
  if (!onRename) {
    return (
      <button
        type="button"
        className={clsx(
          'group inline-flex items-center gap-2 min-w-0 max-w-full text-left',
          'hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer',
        )}
        title={t('board:document_preview.click_to_copy', { defaultValue: 'Click to copy filename' })}
        onClick={(e) => { stop(e); copyName() }}
      >
        <span className={clsx('text-lg font-semibold truncate', className)}>{name}</span>
        <Copy
          className={clsx(
            'shrink-0 opacity-0 group-hover:opacity-50 transition-opacity',
            iconClassName ?? 'w-4 h-4',
          )}
        />
      </button>
    )
  }

  // ── Rename mode (board-name parity) ──
  const beginEditing = () => {
    editingFromRef.current = name
    setDraft(name)
    setIsEditing(true)
    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
  }

  const commit = () => {
    if (actionInProgressRef.current) return
    actionInProgressRef.current = true
    const sanitized = trimAndClamp(draft, maxLength)
    if (sanitized && sanitized !== editingFromRef.current) onRename(sanitized)
    setIsEditing(false)
    setTimeout(() => { actionInProgressRef.current = false }, 0)
  }

  const cancel = () => {
    if (actionInProgressRef.current) return
    actionInProgressRef.current = true
    setIsEditing(false)
    setTimeout(() => { actionInProgressRef.current = false }, 0)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation() // don't let viewer/board shortcuts fire while typing
    if (e.key === 'Enter') { e.preventDefault(); commit() }
    else if (e.key === 'Escape') { e.preventDefault(); cancel() }
  }

  if (isEditing) {
    return (
      <span className="group inline-flex items-center gap-2 min-w-0 max-w-full">
        <InlineRenameInput
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          onClick={stop}
          onPointerDown={stop}
          maxLength={maxLength}
          // Same typography classes as the display span so the field keeps
          // its exact footprint while editing.
          className={clsx('text-lg font-semibold truncate', className)}
        />
        {/* Invisible copy-icon placeholder: holds the icon's slot so the
            control's width doesn't shrink while editing. */}
        <Copy aria-hidden className={clsx('shrink-0 invisible', iconClassName ?? 'w-4 h-4')} />
      </span>
    )
  }

  return (
    <span className="group inline-flex items-center gap-2 min-w-0 max-w-full">
      <span
        role="button"
        tabIndex={0}
        className={clsx(
          'text-lg font-semibold truncate cursor-text hover:text-blue-600 dark:hover:text-blue-400 transition-colors',
          className,
        )}
        title={t('board:actions.click_to_rename', { defaultValue: 'Click to rename' })}
        onClick={(e) => { stop(e); beginEditing() }}
        onPointerDown={stop}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            stop(e)
            beginEditing()
          }
        }}
      >
        {name}
      </span>
      <button
        type="button"
        className="shrink-0 opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity"
        title={t('board:document_preview.click_to_copy', { defaultValue: 'Click to copy filename' })}
        aria-label={t('board:document_preview.click_to_copy', { defaultValue: 'Click to copy filename' })}
        onClick={(e) => { stop(e); copyName() }}
        onPointerDown={stop}
      >
        <Copy className={clsx('text-muted-foreground', iconClassName ?? 'w-4 h-4')} />
      </button>
    </span>
  )
}
